import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {EntityType, HistoryEntry, HistoryEventType, ReferenceBookResponse} from '@npck/af-frontend-api';
import {RouterService} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {INCIDENT_ID, REFUND_ID, SUBJECT_ID, TRANSACTION_ID} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {NavigationTarget} from '../entity-history.component';
import {FIELD_DICTIONARY, IGNORED_FIELDS, LINK_FIELDS} from '../field-groups';
import {ValueFormatter} from '../value-formatter';

type HistoryChange = HistoryEntry['changes'][number];

@Component({
    selector: 'af-change-detail-dialog',
    templateUrl: 'change-detail-dialog.component.html',
    styleUrls: ['change-detail-dialog.component.scss'],
    standalone: false,
})
export class ChangeDetailDialogComponent {
    readonly eventType: HistoryEventType;
    readonly entityType: EntityType;
    readonly titleKey: string;

    groups: FieldGroupView[] = [];
    changedFields: ChangedFieldRow[] = [];
    ready = false;

    private refMap = new Map<string, string>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ChangeDetailDialogData,
        public dialogRef: MatDialogRef<ChangeDetailDialogComponent>,
        private valueFormatter: ValueFormatter,
        private translate: TranslateService,
        private dictionaryHolder: DictionaryHolderService,
        private routerService: RouterService,
    ) {
        this.eventType = data.entry.eventType;
        this.entityType = data.entry.entityType;
        this.titleKey = this.resolveTitleKey();
        this.preloadAndBuild();
    }

    close(): void {
        this.dialogRef.close();
    }

    navigateTo(target: NavigationTarget | null): void {
        if (!target) {
            return;
        }
        this.dialogRef.close();
        this.routerService.navigate(target.path, {pathParams: target.pathParams, queryParams: target.queryParams});
    }

    private resolveTitleKey(): string {
        switch (this.eventType) {
            case HistoryEventType.CREATED:
                return `i18n_af_history_${this.entityType}_created`;
            case HistoryEventType.DELETED:
                return `i18n_af_history_${this.entityType}_deleted`;
            case HistoryEventType.UPDATED:
                return `i18n_af_history_${this.entityType}_updated`;
            default:
                return '';
        }
    }

    private preloadAndBuild(): void {
        const refTypes = this.detectNeededRefTypes();
        if (refTypes.length === 0) {
            this.buildViews();
            this.ready = true;
            return;
        }
        combineLatest(refTypes.map(type => this.dictionaryHolder.getReference(type)))
            .subscribe({
                next: responses => {
                    refTypes.forEach((type, i) => this.cacheReferences(type, responses[i]));
                    this.buildViews();
                    this.ready = true;
                },
                error: () => {
                    this.buildViews();
                    this.ready = true;
                },
            });
    }

    private detectNeededRefTypes(): string[] {
        const propertyNames = this.eventType === HistoryEventType.UPDATED
            ? this.data.entry.changes.map((change: HistoryChange) => change.property)
            : Object.keys(this.data.entry.fullSnapshot ?? {});
        const result = new Set<string>();
        propertyNames.forEach((prop: string) => {
            const refType = FIELD_DICTIONARY[`${this.entityType}.${prop}`];
            if (refType) {
                result.add(refType);
            }
        });
        return [...result];
    }

    private cacheReferences(refType: string, items: ReferenceBookResponse[]): void {
        items.forEach(item => {
            const raw = item.name_ru || item.code;
            const display = this.translate.instant(raw);
            this.refMap.set(`${refType}#${item.code}`, display);
        });
    }

    private buildViews(): void {
        if (this.eventType === HistoryEventType.UPDATED) {
            const rows: ChangedFieldRow[] = this.data.entry.changes
                .filter((change: HistoryChange) => !this.isEmptyChange(change.oldValue, change.newValue))
                .map((change: HistoryChange) => ({
                    property: change.property,
                    label: this.fieldLabel(change.property),
                    oldValue: this.formatValue(change.property, change.oldValue),
                    newValue: this.formatValue(change.property, change.newValue),
                }));
            this.changedFields = this.orderWithCurrencyPairs(rows);
        } else {
            this.groups = this.buildGroups(this.data.entry.fullSnapshot ?? {});
        }
    }

    private isEmptyChange(oldValue: unknown, newValue: unknown): boolean {
        const isEmpty = (v: unknown): boolean =>
            v === null || v === undefined || v === '' ||
            (Array.isArray(v) && v.length === 0);
        return isEmpty(oldValue) && isEmpty(newValue);
    }

    private buildGroups(snapshot: Record<string, unknown>): FieldGroupView[] {
        const linkFields = new Set(LINK_FIELDS[this.entityType] ?? []);
        const basicRows: FieldRow[] = [];
        const linkRows: FieldRow[] = [];

        Object.entries(snapshot)
            .filter(([key, value]) => !IGNORED_FIELDS.has(key) && value !== null && value !== undefined)
            .forEach(([key, value]) => {
                const row = this.toRow(key, value);
                if (linkFields.has(key)) {
                    row.navigationTarget = this.fieldNavigationTarget(key, String(value), snapshot);
                    linkRows.push(row);
                } else {
                    basicRows.push(row);
                }
            });

        const byLabel = (a: FieldRow, b: FieldRow): number => a.label.localeCompare(b.label, 'ru');
        const result: FieldGroupView[] = [];
        if (basicRows.length > 0) {
            result.push({titleKey: 'i18n_af_history_group_basic', rows: this.orderWithCurrencyPairs(basicRows)});
        }
        const sortedLinks = [this.ownIdRow(), ...linkRows.sort(byLabel)];
        result.push({titleKey: 'i18n_af_history_group_links', rows: sortedLinks});

        const attachmentsGroup = this.buildAttachmentsGroup(snapshot);
        if (attachmentsGroup) {
            result.push(attachmentsGroup);
        }

        return result;
    }

    private buildAttachmentsGroup(snapshot: Record<string, unknown>): FieldGroupView | null {
        const additional = snapshot.additionalData as {attachments?: Array<Record<string, unknown>>} | undefined | null;
        const attachments = additional?.attachments;
        if (!Array.isArray(attachments) || attachments.length === 0) {
            return null;
        }
        const rows: FieldRow[] = attachments.map((attachment, index) => {
            const fallback = this.translate.instant('i18n_af_history_attachment_fallback', {number: index + 1});
            const fileName = (attachment.fileName ?? attachment.name ?? fallback) as string;
            const documentType = attachment.documentType as string | undefined;
            return {
                property: `attachment_${index}`,
                label: documentType
                    ? this.translate.instant(`i18n_af_attachedDocumentType#${documentType}`)
                    : fallback,
                value: fileName,
            };
        });
        return {titleKey: 'i18n_af_history_group_attachments', rows};
    }

    private toRow(property: string, value: unknown): FieldRow {
        return {
            property,
            label: this.fieldLabel(property),
            value: this.formatValue(property, value),
        };
    }

    private formatValue(property: string, value: unknown): string {
        if (value === null || value === undefined) {
            return '—';
        }
        const refType = FIELD_DICTIONARY[`${this.entityType}.${property}`];
        if (refType) {
            return this.resolveReference(refType, value);
        }
        return this.valueFormatter.format(this.entityType, property, value, this.eventType);
    }

    private resolveReference(refType: string, value: unknown): string {
        const codes = Array.isArray(value) ? value : String(value).split(',');
        return codes
            .map((code: unknown) => String(code).trim())
            .map((code: string) => this.refMap.get(`${refType}#${code}`) ?? code)
            .join(', ');
    }

    private ownIdRow(): FieldRow {
        const camel = this.entityType.toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
        const pascal = camel.charAt(0).toUpperCase() + camel.slice(1) + 'Id';
        const key = `i18n_af_fieldLabel${pascal}`;
        const translated = this.translate.instant(key);
        return {
            property: 'id',
            label: translated === key ? 'ID' : translated,
            value: this.data.entry.entityId,
            navigationTarget: this.data.navigationTarget,
        };
    }

    private fieldNavigationTarget(property: string, value: string, snapshot: Record<string, unknown>): NavigationTarget | null {
        switch (property) {
            case 'refundId':
                return {path: Path.REFUND_DETAILS, pathParams: {[REFUND_ID]: value}};
            case 'subjectId':
                return {path: Path.SUBJECT_DETAILS, pathParams: {[SUBJECT_ID]: value}};
            case 'transactionId': {
                const incidentId = snapshot.incidentId;
                return typeof incidentId === 'string'
                    ? {
                        path: Path.INCIDENTS_TRANSACTIONS_DETAILS,
                        pathParams: {[INCIDENT_ID]: incidentId, [TRANSACTION_ID]: value},
                    }
                    : null;
            }
            default:
                return null;
        }
    }

    private fieldLabel(property: string): string {
        const specific = `i18n_af_field_${this.entityType}_${property}`;
        const specificT = this.translate.instant(specific);
        if (specificT !== specific) {
            return specificT;
        }
        const pascal = property.charAt(0).toUpperCase() + property.slice(1);
        const global = `i18n_af_fieldLabel${pascal}`;
        const globalT = this.translate.instant(global);
        if (globalT !== global) {
            return globalT;
        }
        return property;
    }

    private orderWithCurrencyPairs<T extends {property: string; label: string}>(rows: T[]): T[] {
        const byProperty = new Map(rows.map(r => [r.property, r] as [string, T]));
        const amountToCurrency = new Map<string, string>();
        const claimedCurrencies = new Set<string>();
        rows.forEach(row => {
            const currencyProp = this.findCurrencyCounterpart(row.property, byProperty);
            if (currencyProp) {
                amountToCurrency.set(row.property, currencyProp);
                claimedCurrencies.add(currencyProp);
            }
        });

        const sorted = rows
            .filter(row => !claimedCurrencies.has(row.property))
            .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

        const result: T[] = [];
        sorted.forEach(row => {
            result.push(row);
            const currencyProp = amountToCurrency.get(row.property);
            const currencyRow = currencyProp ? byProperty.get(currencyProp) : undefined;
            if (currencyRow) {
                result.push(currencyRow);
            }
        });
        return result;
    }

    private findCurrencyCounterpart<T>(property: string, byProperty: Map<string, T>): string | null {
        if (property.startsWith('amount')) {
            const candidate = property === 'amount'
                ? 'currency'
                : 'currency' + property.charAt(0).toUpperCase() + property.slice(1);
            if (byProperty.has(candidate)) {
                return candidate;
            }
        }
        if (property.endsWith('Amount')) {
            const candidate = property.replace(/Amount$/, 'Currency');
            if (byProperty.has(candidate)) {
                return candidate;
            }
        }
        return null;
    }
}

export interface ChangeDetailDialogData {
    entry: HistoryEntry;
    navigationTarget: NavigationTarget | null;
}

interface FieldRow {
    property: string;
    label: string;
    value: string;
    navigationTarget?: NavigationTarget | null;
}

interface FieldGroupView {
    titleKey: string;
    rows: FieldRow[];
}

interface ChangedFieldRow {
    property: string;
    label: string;
    oldValue: string;
    newValue: string;
}
